import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { testId } from '@jwp/ott-common/src/utils/common';
import { useNavigate, useLocation } from 'react-router';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { renderToString } from 'react-dom/server';
import type { PlaylistItem, videoDetails } from '@jwp/ott-common/types/playlist';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import { useAppInfo } from '@jwp/ott-hooks-react/src/useAppInfo';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_left.svg?react';
import { useTranslation } from 'react-i18next';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';

import ErrorPage from '../ErrorPage/ErrorPage';
import IconButton from '../IconButton/IconButton';
import Icon from '../Icon/Icon';
import CollapsibleText from '../CollapsibleText/CollapsibleText';
import { useAccountStore } from '../../../../common/src/stores/AccountStore';
import { useTheme } from '../../../../../ThemeContext';
import { handleBackNavigate } from '../../utils/handleBackNavigate';

import styles from './VideoDetails.module.scss';

type Props = {
  title: string;
  description: string;
  primaryMetadata: React.ReactNode;
  movieGenreMetadata?: string[];
  secondaryMetadata?: React.ReactNode;
  image?: string;
  startWatchingButton: React.ReactNode;
  shareButton: React.ReactNode;
  favoriteButton?: React.ReactNode;
  trailerButton?: React.ReactNode;
  children: React.ReactNode;
  item: PlaylistItem | videoDetails;
};

const VideoDetails: React.VFC<Props> = ({
  title,
  description,
  primaryMetadata,
  secondaryMetadata,
  movieGenreMetadata,
  image,
  startWatchingButton,
  shareButton,
  favoriteButton,
  trailerButton,
  children,
  item,
}) => {
  const breakpoint: Breakpoint = useBreakpoint();
  const [isImageNotLoaded, setIsImageNotLoaded] = useState(false);
  const isMobile = breakpoint === Breakpoint.xs;
  const alt = '';
  const params = new URLSearchParams(window.location.search);
  const channel = params.get('channel');
  const [is16x9, setIs16x9] = useState<boolean | null>(null);

  const { epgNoProgramText, epgEmptySlotText, epgDisabledMessage, heroBanner } = useUIControls();
  const { landscapeThumbnail } = useAppInfo();
  const { isEpgEnable } = useAppConfig();
  const { click } = useContentStateStore.getState();
  const screenClass = click?.label;
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { universalStyles } = useTheme();

  const metadataString = renderToString(primaryMetadata);

  const showMoreClickHandler = () => {
    const newState = { item: item, primaryMetadata: metadataString };
    sessionStorage.setItem('seeMoreDetails', JSON.stringify(newState));

    navigate(modalURLFromLocation(location, 'show-more-details'), {
      state: newState,
    });
  };

  if (channel && !isEpgEnable) {
    return <ErrorPage title={epgDisabledMessage} />;
  }

  const getBannerOption = (option: string | undefined) => {
    switch (option) {
      case 'option1':
        return styles.posterOption1;
      case 'option2':
        return styles.posterOption2;
      case 'option3':
        return styles.posterOption3;
      case 'option4':
        return styles.posterOption4;
      default:
        return styles.posterOption1;
    }
  };

  const handleImageLoad = (event: any) => {
    const width = event.target.naturalWidth;
    const height = event.target.naturalHeight;
    if (width / height === 16 / 9) {
      setIs16x9(true);
    } else {
      setIs16x9(false);
    }
  };

  useEffect(() => {
    sendGAEvent('screen_view', {
      firebase_screen_class: screenClass,
      firebase_screen: `${title}`,
      device_type: deviceType,
      userid_no: userId,
    });
  }, [title]);

  return (
    <div data-testid={testId('cinema-layout')}>
      <header className={styles.video} data-testid={testId('video-details')} id="video-details">
        <div className={classNames(styles.main, styles.mainPadding)}>
          <img
            className={is16x9 ? getBannerOption(heroBanner) : getBannerOption('option4')}
            src={isImageNotLoaded ? landscapeThumbnail : image ?? landscapeThumbnail}
            alt={alt}
            onLoad={handleImageLoad}
            onError={() => {
              setIsImageNotLoaded(true);
            }}
          />
          {!is16x9 && (
            <>
              <div className={styles.posterFadeMenu} />
              <div className={styles.posterFadeLeft} />
              <div className={styles.posterFadeBottom} />
              <div className={styles.posterBackground} />
            </>
          )}

          <IconButton aria-label={t('common:back')} onClick={() => handleBackNavigate(navigate)} className={styles.backButton}>
            <Icon icon={ArrowLeft} />
          </IconButton>
          <div className={styles.info}>
            <h1 className={styles.title} style={universalStyles.videoDetailsTitle}>
              {title}
            </h1>
            {title == epgNoProgramText || title == epgEmptySlotText ? null : (
              <div className={styles.metaContainer}>
                <div className={styles.primaryMetadata}>{primaryMetadata}</div>
                {secondaryMetadata && <div className={styles.secondaryMetadata}>{secondaryMetadata}</div>}
                {movieGenreMetadata && movieGenreMetadata.length > 0 && (
                  <div className={styles.genresContainer}>
                    {movieGenreMetadata &&
                      movieGenreMetadata.length > 0 &&
                      movieGenreMetadata.map((item, index) => (
                        <div className={styles.genres} key={index}>
                          <span style={universalStyles.genresChipContainerText}>{item}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            <CollapsibleText
              item={item}
              text={description}
              className={styles.description}
              maxHeight={isMobile ? 60 : 120}
              showMoreClickHandler={showMoreClickHandler}
            />
            {item && item?.audio_language && (
              <div className={styles.audioLanguage}>
                <span>{t('common:show_more.audio_languages')}:</span>
                <span>{item.audio_language}</span>
              </div>
            )}
            {title == epgNoProgramText || title == epgEmptySlotText ? null : (
              <div className={styles.buttonBar}>
                {startWatchingButton}
                {trailerButton}
                {favoriteButton}
                {shareButton}
              </div>
            )}
          </div>
        </div>
      </header>
      {children}
    </div>
  );
};

export default VideoDetails;
